import React, { useState } from 'react';
import { useEffect } from 'react';
import { trackMixPanelEvent } from './ApiService'
// FieldComponents.js - Handles field manipulation functionality

/**
 * Updates a field's type
 * @param {Array} formFields - Current form fields
 * @param {number} index - Index of the field to update
 * @param {string} newType - New field type
 * @returns {Array} Updated form fields
 */
export const updateFieldType = (formFields, index, newType) => {
  const updatedFields = [...formFields];
  updatedFields[index] = {
    ...updatedFields[index],
    type: newType
  };
  return updatedFields;
};

/**
 * Updates a field's label
 * @param {Array} formFields - Current form fields
 * @param {number} index - Index of the field to update
 * @param {string} newLabel - New field label
 * @returns {Array} Updated form fields
 */
export const updateFieldLabel = (formFields, index, newLabel) => {
  const updatedFields = [...formFields];
  updatedFields[index] = {
    ...updatedFields[index],
    label: newLabel
  };
  return updatedFields;
};

/**
 * Deletes a field
 * @param {Array} formFields - Current form fields
 * @param {number} index - Index of the field to delete
 * @returns {Array} Updated form fields
 */
export const deleteField = (formFields, index) => {
  const updatedFields = [...formFields];
  updatedFields.splice(index, 1);
  return updatedFields;
};

/**
 * Adds a choice to a choice field
 * @param {Array} formFields - Current form fields
 * @param {number} fieldIndex - Index of the field
 * @returns {Array} Updated form fields
 */
export const addChoice = (formFields, fieldIndex) => {
  const updatedFields = [...formFields];
  const field = { ...updatedFields[fieldIndex] };
  
  if (!field.choices) {
    field.choices = [];
  }
  
  const newChoiceNum = field.choices.length + 1;
  field.choices = [
    ...field.choices,
    { 
      label: `Option ${newChoiceNum}`, 
      value: `option_${newChoiceNum}`.toLowerCase()
    }
  ];
  
  updatedFields[fieldIndex] = field;
  return updatedFields;
};

/**
 * Updates a choice's label
 * @param {Array} formFields - Current form fields
 * @param {number} fieldIndex - Index of the field
 * @param {number} choiceIndex - Index of the choice
 * @param {string} newLabel - New choice label
 * @returns {Array} Updated form fields
 */
export const updateChoiceLabel = (formFields, fieldIndex, choiceIndex, newLabel) => {
  const updatedFields = [...formFields];
  const field = { ...updatedFields[fieldIndex] };
  const choices = [...field.choices];
  
  choices[choiceIndex] = {
    ...choices[choiceIndex],
    label: newLabel,
    value: newLabel.toLowerCase().replace(/\\s+/g, '_')
  };
  
  field.choices = choices;
  updatedFields[fieldIndex] = field;
  return updatedFields;
};

/**
 * Deletes a choice
 * @param {Array} formFields - Current form fields
 * @param {number} fieldIndex - Index of the field
 * @param {number} choiceIndex - Index of the choice
 * @returns {Array} Updated form fields
 */
export const deleteChoice = (formFields, fieldIndex, choiceIndex) => {
  const updatedFields = [...formFields];
  const field = { ...updatedFields[fieldIndex] };
  const choices = [...field.choices];
  
  choices.splice(choiceIndex, 1);
  
  // If all choices are removed, add a default one
  if (choices.length === 0) {
    choices.push({ label: "Option 1", value: "option_1" });
  }
  
  field.choices = choices;
  updatedFields[fieldIndex] = field;
  return updatedFields;
};

/**
 * Renders a single field item component
 * @param {Object} field - The field to render
 * @param {number} index - Field index
 * @param {Function} onLabelChange - Handler for label changes
 * @param {Function} onTypeChange - Handler for type changes
 * @param {Function} onDelete - Handler for deletion
 * @param {Function} onAddChoice - Handler for adding choices
 * @param {Function} onChoiceLabelChange - Handler for changing choice labels
 * @param {Function} onDeleteChoice - Handler for deleting choices
 * @returns {JSX.Element} Field item component
 */
export const FieldItem = ({
  field,
  index,
  onLabelChange,
  onTypeChange,
  onDelete,
  onAddChoice,
  onChoiceLabelChange,
  onDeleteChoice
}) => {
  return (
    <div className="field-item">
      <input 
        type="text"
        value={field.label || ''}
        onChange={(e) => onLabelChange(index, e.target.value)}
        placeholder="Field name"
        className="field-label-input"
      />
      <div className="field-controls">
        <select 
          value={field.type || 'TextField'} 
          onChange={(e) => onTypeChange(index, e.target.value)}
          className="field-type-select"
        >
          <option value="TextField">TextField</option>
          <option value="NumericField">NumericField</option>
          <option value="YesNoField">YesNoField</option>
          <option value="DateTimeField">DateTimeField</option>
          <option value="Label">Label</option>
          <option value="TimeField">TimeField</option>
          <option value="ChoiceField">ChoiceField</option>
          <option value="MultiChoiceField">MultiChoiceField</option>
          <option value="AddressField">AddressField</option>
          <option value="SignatureField">SignatureField</option>
          <option value="PhotoField">PhotoField</option>
        </select>
        <button 
          onClick={() => onDelete(index)}
          className="delete-field-button"
          title="Delete field"
        >
          ×
        </button>
      </div>
      {(field.type === 'ChoiceField' || field.type === 'MultiChoiceField') && field.choices && (
        <div className="choices-list">
          <div className="choices-header">
            <span className="choices-label">Options:</span>
            <button
              onClick={() => onAddChoice(index)}
              className="add-choice-button"
              title="Add option"
            >
              + Add Option
            </button>
          </div>
          <ul className="choices-items">
            {field.choices.map((choice, choiceIndex) => (
              <li key={choiceIndex} className="choice-item">
                <input 
                  type="text"
                  value={choice.label || ''}
                  onChange={(e) => onChoiceLabelChange(index, choiceIndex, e.target.value)}
                  placeholder="Option label"
                  className="choice-label-input"
                />
                <button
                  onClick={() => onDeleteChoice(index, choiceIndex)}
                  className="delete-choice-button"
                  title="Delete option"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Renders a list of all fields
 * @param {Array} formFields - Array of form fields
 * @param {Object} handlers - Object containing handler functions
 * @returns {JSX.Element} Fields list component
 */
export const FieldsList = ({ formFields, handlers }) => {
  const [userModifiedFields, setUserModifiedFields] = useState(false);

  // Wrap handlers to set userModifiedFields when a change occurs
  const handleLabelChange = (index, value) => {
    setUserModifiedFields(true);
    handlers.onLabelChange(index, value);
  };

  const handleTypeChange = (index, value) => {
    setUserModifiedFields(true);
    handlers.onTypeChange(index, value);
  };

  const handleDelete = (index) => {
    setUserModifiedFields(true);
    handlers.onDelete(index);
  };

  const handleAddChoice = (index) => {
    setUserModifiedFields(true);
    handlers.onAddChoice(index);
  };

  const handleChoiceLabelChange = (fieldIndex, choiceIndex, value) => {
    setUserModifiedFields(true);
    handlers.onChoiceLabelChange(fieldIndex, choiceIndex, value);
  };

  const handleDeleteChoice = (fieldIndex, choiceIndex) => {
    setUserModifiedFields(true);
    handlers.onDeleteChoice(fieldIndex, choiceIndex);
  };

  useEffect(() => {
    if (userModifiedFields) {
      trackMixPanelEvent('Instaform Result Modified', {
        result: true
      });
      setUserModifiedFields(false);
    }
  }, [userModifiedFields]);

  if (formFields.length === 0) {
    return (
      <div className="no-fields-message">
        <p>Upload an image or PDF to detect form fields</p>
      </div>
    );
  }

  return (
    <div className="fields-panel">
      <div className="fields-list">
        {formFields.map((field, index) => (
          <FieldItem
            key={index}
            field={field}
            index={index}
            onLabelChange={handleLabelChange}
            onTypeChange={handleTypeChange}
            onDelete={handleDelete}
            onAddChoice={handleAddChoice}
            onChoiceLabelChange={handleChoiceLabelChange}
            onDeleteChoice={handleDeleteChoice}
          />
        ))}
      </div>
    </div>
  );
};